import { createContext, useEffect, useState, useCallback } from 'react';
import type { ReactNode } from 'react';
import type { Theme } from '@/shared/lib/theme';

export type { Theme };

const STORAGE_KEY = 'saas-theme';

export interface ThemeContextValue {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  /** The resolved effective theme (light or dark) — useful for icons. */
  resolvedTheme: 'light' | 'dark';
}

export const ThemeContext = createContext<ThemeContextValue | null>(null);

interface ThemeProviderProps {
  children?: ReactNode;
  /** Initial theme. Defaults to 'system'. */
  defaultTheme?: Theme;
}

/**
 * Real ThemeProvider implementation.
 *
 * - Reads the persisted theme from localStorage on mount (hydrated synchronously
 *   via lazy initializer to avoid a flash of wrong theme).
 * - Applies the `dark` CSS class to <html> when the effective theme is dark.
 * - Resolves `system` to light or dark via the `prefers-color-scheme` media query.
 * - Listens for OS-level preference changes and re-resolves when mode is `system`.
 * - Persists the user choice to localStorage on every setTheme call.
 */
export function ThemeProvider({ children, defaultTheme = 'system' }: ThemeProviderProps) {
  const [theme, setThemeState] = useState<Theme>(() => {
    if (typeof window === 'undefined') return defaultTheme;
    const stored = window.localStorage.getItem(STORAGE_KEY) as Theme | null;
    return stored ?? defaultTheme;
  });

  /** Compute the effective (resolved) theme for a given mode. */
  const resolveTheme = useCallback((t: Theme): 'light' | 'dark' => {
    if (t === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light';
    }
    return t;
  }, []);

  const [resolvedTheme, setResolvedTheme] = useState<'light' | 'dark'>(() => {
    if (typeof window === 'undefined') return 'light';
    const stored = window.localStorage.getItem(STORAGE_KEY) as Theme | null;
    const initial = stored ?? defaultTheme;
    if (initial === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return initial;
  });

  /** Apply / remove the `dark` class on <html>. */
  const applyTheme = useCallback((resolved: 'light' | 'dark') => {
    const root = window.document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(resolved);
    setResolvedTheme(resolved);
  }, []);

  /** Apply theme whenever the state changes. */
  useEffect(() => {
    applyTheme(resolveTheme(theme));
  }, [theme, applyTheme, resolveTheme]);

  /** Handle OS preference changes when mode is `system`. */
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = () => {
      // Only re-apply if currently in system mode
      setThemeState((current) => {
        if (current === 'system') {
          applyTheme(mediaQuery.matches ? 'dark' : 'light');
        }
        return current;
      });
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [applyTheme]);

  const setTheme = useCallback((next: Theme) => {
    window.localStorage.setItem(STORAGE_KEY, next);
    setThemeState(next);
    applyTheme(resolveTheme(next));
  }, [applyTheme, resolveTheme]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, resolvedTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}
